import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@nosible/shared/components/ui/dialog";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nosible/shared/components/ui/select";
import { UserPlus, Loader2, Mail } from "lucide-react";
import { trpc } from "@/admin/lib/trpc";

type AdminInviteMemberProps = {
  organizationId: string;
  organizationName: string;
};

const ROLES = ["member", "admin", "owner"] as const;
type Role = (typeof ROLES)[number];

export function AdminInviteMember({
  organizationId,
  organizationName,
}: AdminInviteMemberProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [success, setSuccess] = useState(false);

  const utils = trpc.useUtils();

  const inviteMutation = trpc.admin.orgInvite.useMutation({
    onSuccess: () => {
      setSuccess(true);
      utils.admin.orgBySlug.invalidate();
      setTimeout(() => {
        setEmail("");
        setRole("member");
        setSuccess(false);
        setOpen(false);
      }, 2000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate({
      email: email.trim(),
      role,
      organizationId,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setEmail("");
      setRole("member");
      setSuccess(false);
      inviteMutation.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm">
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join {organizationName}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-neon-green/10 flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-neon-green" />
            </div>
            <p className="text-silica font-medium">Invitation sent!</p>
            <p className="text-chromium-mist text-sm mt-1">
              An invitation has been sent to {email}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@example.com"
                required
                disabled={inviteMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as Role)}
                disabled={inviteMutation.isPending}
              >
                <SelectTrigger id="invite-role" className="w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="capitalize">{r}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-chromium-mist">
                Members can view and use organization resources. Admins can also manage members and settings. Owners have full control.
              </p>
            </div>

            {inviteMutation.error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                <p className="text-sm text-red-400">{inviteMutation.error.message}</p>
              </div>
            )}

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={inviteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={inviteMutation.isPending || !email.trim()}
              >
                {inviteMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
